import { useAddItem, useCart } from '@framework/cart'
import { Button } from '@components/ui'
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import TooltipModal from '../Tooltip/TooltipDiamondPage'
import { ProductVariant } from '@commerce/types/product'
import { IDiamond } from 'types/diamond.types'
import { useRouter } from 'next/router'
import useLocalStorage from 'hooks/useLocalStorage'
import { createDiamondProduct } from './helpers'

interface Props {
  productVariant?: ProductVariant
  diamond?: IDiamond
  displayCartError?: Function
  closeModal?: Function
}

const PurchaseOptionsView: React.FC<Props> = ({
  productVariant,
  diamond,
  displayCartError,
  closeModal,
}) => {
  const router = useRouter()
  const [showTooltip, setShowTooltip] = useState(false)
  const [loadingDiamond, setLoadingDiamond] = useState(false)
  const [
    loadingProductWithNaturalDiamond,
    setLoadingProductWithNaturalDiamond,
  ] = useState(false)
  const [loadingProductWithLabDiamond, setLoadingProductWithLabDiamond] =
    useState(false)
  const [loadingSetting, setLoadingSetting] = useState(false)
  const [loadingPendant, setLoadingPendant] = useState(false)
  const [diamondSelect, setDiamondSelect] = useLocalStorage('diamond', diamond)
  const [settingSelect, setSettingSelect] = useLocalStorage(
    'setting',
    productVariant
  )

  const addItem = useAddItem()
  const { data } = useCart()

  const handleClickDiamond = () => {
    setDiamondSelect(diamond)
  }

  const handleClickProduct = () => {
    setSettingSelect(productVariant)
  }

  const addToCart = async () => {
    if (diamond && data) {
      setLoadingDiamond(true)

      try {
        const response = await createDiamondProduct(
          diamond.id.toString(),
          data.customerId
        )
        const { product_id, variant_id } = response.data
        await addItem({
          productId: String(product_id),
          variantId: String(variant_id),
        })

        if (window.gtag) {
          window.gtag('event', 'add_to_cart', {
            currency: 'USD',
            value: parseFloat(diamond.price || '0'),
            items: [
              {
                item_id: `YD${diamond.id}`,
                item_name: `${diamond.weight} CARAT ${diamond.shape} ${
                  diamond.labcreated == true ? 'LAB' : 'NATURAL'
                } DIAMOND`,
                item_category: 'Diamonds',
                affiliation: 'Yadav Diamonds and Jewelry',
                item_brand: 'Yadav Diamonds and Jewelry',
                location_id: `${process.env.showroomPlaceId}`,
                price: parseFloat(diamond.price || '0'),
                quantity: 1,
              },
            ],
          })
        }

        router.push('/cart')
        // TODO: handle this error
      } catch (err) {
        if (displayCartError !== undefined) {
          displayCartError()
        }
      }
    }
  }

  if (productVariant) {
    return (
      <div className='"relative bg-white rounded-md outline-none"'>
        <div className="p-4 border-b border-solid border-gray-400">
          <h3 className="text-xl md:text-2xl uppercase">
            Great choice! now we need a nice stone
          </h3>
        </div>
        <div className="flex text-sm mt-2 ml-4">
          <div>
            <span className="mr-1">Combine this setting with a </span>
          </div>
          <div
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <FontAwesomeIcon
              icon={faCircleQuestion}
              className="cursor-pointer"
            />
            {showTooltip && (
              <TooltipModal
                message="Below you can choose between different types of diamonds for your setting."
                className="w-[210px] leading-4"
                arrowStyle="w-3 h-3 rotate-45 bg-white border border-solid drop-shadow-sm border-t-0 border-l-0 -top-1.5 relative z-20"
                position="-bottom-1.5 flex-col-reverse z-50"
                container="relative flex flex-col items-center group"
              >
                {''}
              </TooltipModal>
            )}
          </div>
        </div>{' '}
        {process.env.COMMERCE_CART_ENABLED && (
          <div className="relative p-4">
            <div className="mt-1 mb-4">
              <Button
                aria-label="Natural diamond"
                type="button"
                width="100%"
                loading={loadingProductWithNaturalDiamond}
                variant="type2"
                onClick={() => {
                  handleClickProduct()
                  setLoadingProductWithNaturalDiamond(true)
                  router.push('/natural-loose-diamonds')
                }}
              >
                Natural Diamond
              </Button>
            </div>
            <div className="mt-1">
              <Button
                aria-label="Lab diamond"
                type="button"
                variant="type2"
                width="100%"
                loading={loadingProductWithLabDiamond}
                onClick={() => {
                  handleClickProduct()
                  setLoadingProductWithLabDiamond(true)
                  router.push('/lab-created-diamonds')
                }}
              >
                Lab Diamond
              </Button>
            </div>
          </div>
        )}
      </div>
    )
  } else if (diamond) {
    return (
      <div className='"relative bg-white rounded-md outline-none"'>
        <div className="p-4 border-b border-solid border-gray-400">
          <h3 className="text-xl my-4 uppercase md:hidden">
            what would you like to do with this diamond?
          </h3>
          <h3 className="hidden md:block text-2xl uppercase">
            what would you like to do?
          </h3>
        </div>
        <div className="relative p-4">
          <div className="mt-1 mb-4 block md:hidden">
            <Button
              variant="type2"
              type="button"
              width="100%"
              onClick={() => {
                handleClickDiamond()
                setLoadingSetting(!loadingSetting)
                router.push('/t/create-your-ring')
              }}
              loading={loadingSetting}
            >
              Add to a Ring
            </Button>
          </div>
          <div className="mt-1 mb-4 hidden md:block">
            <Button
              type="button"
              variant="type2"
              width="100%"
              onClick={() => {
                handleClickDiamond()
                setLoadingSetting(!loadingSetting)
                router.push('/t/create-your-ring')
              }}
              loading={loadingSetting}
            >{`Add this ${
              diamond.labcreated ? 'lab' : ''
            } diamond to a ring`}</Button>
          </div>
          {process.env.COMMERCE_CART_ENABLED && diamond && (
            <>
              <div className="mt-1 block md:hidden">
                <Button
                  aria-label="Buy without a setting"
                  type="button"
                  variant="type2"
                  width="100%"
                  onClick={addToCart}
                  loading={loadingDiamond}
                >
                  Buy without a Setting
                </Button>
              </div>
              <div className="mt-1 hidden md:block">
                <Button
                  aria-label={`Buy this ${
                    diamond.labcreated ? 'lab' : ''
                  } diamond without a setting`}
                  type="button"
                  onClick={addToCart}
                  loading={loadingDiamond}
                  variant="type2"
                  width="100%"
                >
                  {`Buy this ${
                    diamond.labcreated ? 'lab' : ''
                  } diamond without a setting`}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    )
  } else {
    return <div></div>
  }
}

export default PurchaseOptionsView
